import { devicePinApi } from '@/api/devicePin';
import { devicesApi, type DeviceView } from '@/api/devices';
import { familyApi } from '@/api/family';
import { supportApi } from '@/api/support';
import { vitrineCardApi, type Capabilities, type CardPinStatus } from '@/api/vitrineCard';
import { vitrineNotificationsApi, type NotificationPrefs } from '@/api/vitrineNotifications';
import { passkeyEnrolledOnDevice, passkeyStatus, passkeySupported } from '@/api/webauthn';
import { warm } from '@/api/warmCache';

/** Everything the profile tab reads before it draws, applied together so no row pops in after the others. */
export interface SettingsProbes {
  family: boolean;
  pin: boolean;
  faceSupported: boolean;
  /** Server's per-device passkey status (V083); the local flag only when the server cannot be asked. */
  faceEnrolled: boolean;
  devices: DeviceView[];
  caps: Capabilities | null;
  cardPin: CardPinStatus | null;
  prefs: NotificationPrefs | null;
}

export const SETTINGS_PROBES_KEY = (showcase: boolean) => `settings-probes:${showcase ? 'showcase' : 'full'}`;
export const CONTACT_CHANNELS_KEY = 'contact-channels';
export const VITRINE_ME_KEY = 'vitrine-me';

export async function loadSettingsProbes(showcase: boolean): Promise<SettingsProbes> {
  const [family, pin, faceSupported, devices, caps, cardPin] = await Promise.all([
    familyApi.access().then(a => a.hasAccess).catch(() => false),
    devicePinApi.status().then(r => r.enabled).catch(() => false),
    passkeySupported().catch(() => false),
    devicesApi.list().catch(() => [] as DeviceView[]),
    showcase ? vitrineCardApi.capabilities().catch(() => null) : Promise.resolve(null),
    showcase ? vitrineCardApi.pinStatus().catch(() => null) : Promise.resolve(null),
  ]);
  const otpRails = !!(caps?.inAppOtp || caps?.pushOtp);
  const [faceEnrolled, prefs] = await Promise.all([
    faceSupported ? passkeyStatus().catch(() => passkeyEnrolledOnDevice()) : Promise.resolve(false),
    otpRails ? vitrineNotificationsApi.getPrefs().catch(() => null) : Promise.resolve(null),
  ]);
  return { family, pin, faceSupported, faceEnrolled, devices, caps, cardPin, prefs };
}

/** Fetches the cabinet profile tab's reads in the background for `clientId`, ahead of its first visit. */
export function warmVitrineProfileTab(clientId: string): void {
  warm(clientId, SETTINGS_PROBES_KEY(true), () => loadSettingsProbes(true));
  warm(clientId, CONTACT_CHANNELS_KEY, () => supportApi.contactChannels());
  warm(clientId, VITRINE_ME_KEY, () => vitrineCardApi.me());
}
